import { ChangeEvent, useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { CheckCircledIcon } from "@radix-ui/react-icons";
import { WebPageService } from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import { phoneLabel, Verified } from "./publicBooking";

const RESEND_AFTER = 60;
const EMPTY = ["", "", "", "", "", ""];

/**
 * What was typed, pasted or filled in, read as the server reads a number: a
 * US number unless it starts with +. "(617) 555-0142" is +16175550142.
 */
function asTyped(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (raw.trim().startsWith("+")) return `+${digits}`;
  return digits.length === 11 && digits.startsWith("1")
    ? `+${digits}`
    : `+1${digits}`;
}

/**
 * The mobile number and its six-digit code. The page moves on by itself on
 * the sixth digit; phones fill the code in from the text in one tap.
 */
export default function PhoneVerifyStep({
  orgId,
  accent,
  verified,
  initialPhone,
  onVerified,
  onChangeNumber,
}: {
  orgId: number;
  accent: string;
  verified?: Verified;
  initialPhone?: string;
  onVerified: (verified: Verified) => void;
  onChangeNumber: () => void;
}) {
  const [phone, setPhone] = useState(verified?.phone ?? initialPhone ?? "");
  const [sentTo, setSentTo] = useState<string>();
  const [code, setCode] = useState(EMPTY);
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  // Enough digits for a mobile number; the server checks the rest.
  const complete = phone.replace(/\D/g, "").length >= 8;

  useEffect(() => {
    if (wait <= 0) return;
    const timer = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const send = () => {
    setBusy(true);
    setError(undefined);
    WebPageService.startBookingVerification({
      requestBody: { org_id: orgId, phone },
    })
      .then(() => {
        setSentTo(phone);
        setCode(EMPTY);
        setWait(RESEND_AFTER);
        setTimeout(() => boxes.current[0]?.focus(), 0);
      })
      .catch((e) =>
        setError(errorText(e, "We couldn't send a code. Try again."))
      )
      .finally(() => setBusy(false));
  };

  const check = (digits: string[]) => {
    setBusy(true);
    setError(undefined);
    WebPageService.checkBookingVerification({
      requestBody: {
        org_id: orgId,
        phone: sentTo ?? phone,
        code: digits.join(""),
      },
    })
      .then((response) =>
        onVerified({ ...response, phone: sentTo ?? phone, at: Date.now() })
      )
      .catch((e) => {
        setError(errorText(e, "That code didn't work. Try again."));
        setCode(EMPTY);
        boxes.current[0]?.focus();
      })
      .finally(() => setBusy(false));
  };

  // Typing or pasting fills the boxes from the one typed in onward.
  const type = (index: number, value: string) => {
    const digits = value
      .replace(/\D/g, "")
      .slice(0, 6 - index)
      .split("");
    if (!digits.length) {
      setCode(code.map((d, i) => (i === index ? "" : d)));
      return;
    }
    const next = [...code];
    digits.forEach((d, i) => (next[index + i] = d));
    setCode(next);
    const last = Math.min(index + digits.length, 5);
    boxes.current[last]?.focus();
    if (next.every(Boolean)) check(next);
  };

  if (verified) {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-600">
          Mobile number
        </label>
        <div
          className="mt-1.5 flex items-center justify-between rounded-lg border px-3.5 py-2.5"
          style={{ borderColor: accent }}
        >
          <span>{phoneLabel(verified.phone)}</span>
          <span
            className="flex items-center gap-1 text-sm font-semibold"
            style={{ color: accent }}
          >
            <CheckCircledIcon /> Verified
          </span>
        </div>
        <button
          type="button"
          onClick={onChangeNumber}
          className="mt-1.5 text-sm text-gray-500 underline"
        >
          Use a different number
        </button>
      </div>
    );
  }

  return (
    <div>
      <label
        htmlFor="booking-phone"
        className="text-xs font-semibold text-gray-600"
      >
        Mobile number
      </label>
      <div className="mt-1.5 flex gap-2">
        {/*
          The app's phone input formats the number as it is typed. Its input
          takes our onChange (inputProps come after its own), so the number
          is read from what was typed, pasted or filled in before the input
          guesses a country from the first digits ("617" is not Australia).
        */}
        <PhoneInput
          country="us"
          value={phone}
          disabled={!!sentTo}
          onEnterKeyPress={() => complete && send()}
          inputProps={{
            id: "booking-phone",
            name: "phone",
            autoComplete: "tel",
            onChange: (e: ChangeEvent<HTMLInputElement>) =>
              setPhone(asTyped(e.target.value)),
          }}
          containerClass="w-full"
          inputClass="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 disabled:bg-gray-50"
          placeholder="+1 (617) 555-0142"
          dropdownStyle={{ display: "none" }}
        />
        {!sentTo && (
          <button
            type="button"
            disabled={!complete || busy}
            onClick={send}
            style={{ backgroundColor: accent }}
            className="shrink-0 rounded-lg px-4 font-semibold text-white disabled:opacity-40"
          >
            Text me a code
          </button>
        )}
      </div>

      {sentTo && (
        <>
          <p className="mt-4 text-sm text-gray-600">
            We texted a code to {phoneLabel(sentTo)}. Didn&apos;t get it? Wait a
            minute and resend.
          </p>
          <div className="mt-2 flex gap-2">
            {code.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (boxes.current[i] = el)}
                value={digit}
                disabled={busy}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => type(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !digit && i > 0)
                    boxes.current[i - 1]?.focus();
                }}
                className="h-12 w-11 rounded-lg border border-gray-300 text-center text-xl font-semibold"
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between text-sm">
            {wait > 0 ? (
              <span className="text-gray-500">
                Resend in 0:{String(wait).padStart(2, "0")}
              </span>
            ) : (
              <button
                type="button"
                onClick={send}
                disabled={busy}
                className="font-semibold"
                style={{ color: accent }}
              >
                Resend the code
              </button>
            )}
            <button
              type="button"
              className="font-semibold"
              style={{ color: accent }}
              onClick={() => {
                setSentTo(undefined);
                setCode(EMPTY);
                setError(undefined);
              }}
            >
              Use a different number
            </button>
          </div>
        </>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
